import React, { useMemo } from "react";
import { Box, ButtonBase, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { T, cardSx } from "utils/backrrTokens";
import AsyncTableListing from "ui-components/AsyncTableListing";

// Cookies each listing reads its filters, tab, page and search from on mount.
const LISTINGS = {
  startups: { path: "/backrr_startups", query: "backrr_startups_query", status: "backrr_startups_status", page: "backrr_startups_page", search: "search_backrr_startups" },
  investors: { path: "/backrr_investors", query: "backrr_investors_query", status: "backrr_investors_status", page: "backrr_investors_page", search: "search_backrr_investors" },
};

// Opens the listing filtered to this campaign and the dashboard range. The listing dates are
// profile-created dates, the dashboard's are signup dates, so counts can differ slightly.
const openListing = (navigate, kind, campaign, range) => {
  const l = LISTINGS[kind];
  Cookies.set(l.query, JSON.stringify({
    signup_utm_campaign: campaign,
    ...(range?.from && range?.to ? { created_from: range.from, created_to: range.to } : {}),
  }));
  Cookies.set(l.status, "all");
  Cookies.set(l.page, 1);
  Cookies.set(l.search, "");
  navigate(l.path);
};

// A count that opens the matching listing; zero has nothing to show, so it stays plain text.
const CountLink = ({ value, onOpen, label }) =>
  value > 0 ? (
    <ButtonBase
      onClick={onOpen}
      aria-label={label}
      sx={{
        font: "inherit",
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        color: T.accent,
        borderRadius: "4px",
        px: "4px",
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {value}
    </ButtonBase>
  ) : (
    <span style={{ fontVariantNumeric: "tabular-nums" }}>{value ?? 0}</span>
  );

// The shared listing component, not a hand-rolled MUI table. Every other table
// in this admin is an AsyncTableListing, and a second table implementation on
// the dashboard meant a second set of header styles, sort affordances and empty
// states to keep in step with it. The rows are already in hand, so pagination
// is off — this is a short list, not a page of results.
//
// Narrow minimums on purpose: this table lives inside a card on a dashboard,
// not on a listing page with the window to itself.
const COLUMNS = [
  { Header: "Campaign", accessor: "campaign", id: "campaign", minWidth: 160 },
  { Header: "Source", accessor: "source", id: "source", minWidth: 120 },
  { Header: "Startups", accessor: "startups", id: "startups", minWidth: 90, align: "center" },
  { Header: "Investors", accessor: "investors", id: "investors", minWidth: 90, align: "center" },
];

const METRICS = [
  { key: "signups", label: "Signups" },
  { key: "startups", label: "Startups" },
  { key: "investors", label: "Investors" },
];

// Below md the same rows become cards. Five columns cannot survive a phone: a
// horizontal scroller there hides the numbers behind a gesture nobody thinks to
// make, and shrinking the columns turns the campaign name into an ellipsis. A
// card gives the name a full line and lays the three counts out beneath it,
// which is the same information read top-down instead of left-right.
const CampaignCards = ({ rows, onOpen }) => (
  <Box sx={{ display: "grid", gap: 1.5 }}>
    {rows.map((row) => (
      <Box key={`${row.campaign}-${row.source}`} sx={{ ...cardSx, padding: "12px 14px" }}>
        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink, wordBreak: "break-word" }}>
          {row.campaign}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 0.5 }}>
          via {row.source}
        </Typography>

        <Box
          sx={{
            marginTop: 1.5,
            paddingTop: 1.5,
            borderTop: `1px solid ${T.border}`,
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
          }}
        >
          {METRICS.map((m) => (
            <Box key={m.key}>
              <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{m.label}</Typography>
              <Typography
                sx={{ fontSize: "0.95rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
              >
                {m.key === "signups" ? (row[m.key] ?? 0) : (
                  <CountLink value={row[m.key] ?? 0} label={`Open ${m.label.toLowerCase()} from ${row.campaign}`} onOpen={() => onOpen(m.key, row)} />
                )}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    ))}
  </Box>
);

// tableSx reaches AsyncTableListing's own card so a caller that already drew a
// card around this one can flatten it, instead of showing a border inside a
// border. The three dashboard variants do exactly that.
// range: the dashboard's { from, to } (YYYY-MM-DD), carried into the listing's date filter.
const CampaignTable = ({ rows, isLoading, tableSx, range }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const onOpen = (kind, row) => openListing(navigate, kind, row.rawCampaign, range);

  const data = useMemo(() => {
    return [...(rows ?? [])]
      // Biggest campaign first: the one that brought the most people is the one
      // worth reading first, and the list is short enough not to need a sort UI.
      .sort((a, b) => (b?.signups ?? 0) - (a?.signups ?? 0))
      .map((row) => ({
        ...row,
        rawCampaign: row?.campaign,
        campaign: row?.campaign || "—",
        source: row?.source || "—",
      }));
  }, [rows]);

  // Desktop cells: the counts become links into the filtered listings.
  const tableData = useMemo(() => data.map((row) => ({
    ...row,
    startups: <CountLink value={row.startups ?? 0} label={`Open startups from ${row.campaign}`} onOpen={() => onOpen("startups", row)} />,
    investors: <CountLink value={row.investors ?? 0} label={`Open investors from ${row.campaign}`} onOpen={() => onOpen("investors", row)} />,
  })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [data, range?.from, range?.to]);

  if (isLoading) return <Box className="skeleton" sx={{ ...cardSx, height: "14rem" }} />;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.75rem", marginBottom: 1.5 }} color={T.inkSubtle}>
        Signups by acquisition campaign, and what those people became. A signup is one person; a
        startup or investor profile is what they went on to create, so the two can exceed the signups
        where one person made both.
      </Typography>

      {data.length === 0 ? (
        <Box sx={{ ...cardSx, padding: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
            No campaign-attributed signups
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 0.5 }}>
            Nobody arrived through a tracked campaign in this range.
          </Typography>
        </Box>
      ) : isCompact ? (
        <CampaignCards rows={data} onOpen={onOpen} />
      ) : (
        // Desktop keeps the shared table, with its own horizontal scroller —
        // the page's scroll container is overflow-x: hidden, so without this a
        // table wider than the card is not clipped-but-scrollable, it is gone.
        <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto", height: "24rem", display: "flex", flexDirection: "column" }}>
          <AsyncTableListing
            columns={COLUMNS}
            data={tableData}
            pagination={false}
            count={data.length}
            // Long campaign lists scroll inside the fixed-height box above, not off the page.
            maxHeight="100%"
            noDataTitle="No campaign-attributed signups"
            noDataSubTitle="Nobody arrived through a tracked campaign in this range."
            sx={tableSx}
          />
        </Box>
      )}
    </Box>
  );
};

export default CampaignTable;
